// VCIN Brand: Always use VCIN_COLORS from constants.js - never use Tailwind blue classes
import React, { useState } from 'react';
import {
  User, Mail, Shield, Calendar, Clock, Building2, Lock, Eye, EyeOff, Loader2, Check, AlertCircle
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';
import { useAuth } from '../../context/AuthContext';
import { VCIN_COLORS } from '../../styles/constants';

// Info Row Component
function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-4 py-4 border-b border-gray-100 last:border-0">
      <div 
        className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
      >
        <Icon className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-500 mb-0.5">{label}</p>
        <p className="text-gray-900 font-medium">{value || '-'}</p>
      </div>
    </div>
  );
}

// Role Badge
function RoleBadge({ role }) {
  return (
    <span 
      className="px-3 py-1 rounded-full text-sm font-semibold"
      style={{ backgroundColor: '#E8F5E9', color: '#2E7D32' }}
    >
      {role?.replace(/_/g, ' ')}
    </span>
  );
}

// Change Password Form
function ChangePasswordForm() {
  const { user: clerkUser } = useUser();
  const [formData, setFormData] = useState({
    current_password: '',
    new_password: '',
    confirm_password: ''
  });
  const [showPasswords, setShowPasswords] = useState({
    current: false,
    new: false,
    confirm: false
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    // Validate
    if (!formData.current_password || !formData.new_password || !formData.confirm_password) {
      setError('All fields are required');
      return;
    }

    if (formData.new_password !== formData.confirm_password) {
      setError('New passwords do not match');
      return;
    }

    if (formData.new_password.length < 8) {
      setError('New password must be at least 8 characters');
      return;
    }

    setLoading(true);
    try {
      await clerkUser.updatePassword({
        currentPassword: formData.current_password,
        newPassword: formData.new_password
      });
      setSuccess(true);
      setFormData({ current_password: '', new_password: '', confirm_password: '' });
    } catch (err) {
      const e0 = err.errors?.[0];
      setError(e0?.longMessage || e0?.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = (field) => {
    setShowPasswords(prev => ({ ...prev, [field]: !prev[field] }));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {success && (
        <div className="flex items-center gap-2 p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
          <Check className="w-4 h-4 flex-shrink-0" />
          Password changed successfully
        </div>
      )}

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Current Password</label>
        <div className="relative">
          <input
            type={showPasswords.current ? 'text' : 'password'}
            value={formData.current_password}
            onChange={(e) => setFormData({ ...formData, current_password: e.target.value })}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
            style={{ '--tw-ring-color': VCIN_COLORS.primary }}
            placeholder="Enter current password"
            data-testid="current-password-input"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('current')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPasswords.current ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">New Password</label>
        <div className="relative">
          <input
            type={showPasswords.new ? 'text' : 'password'}
            value={formData.new_password}
            onChange={(e) => setFormData({ ...formData, new_password: e.target.value })}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
            style={{ '--tw-ring-color': VCIN_COLORS.primary }}
            placeholder="Enter new password (min 8 characters)"
            data-testid="new-password-input"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('new')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPasswords.new ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Confirm New Password</label>
        <div className="relative">
          <input
            type={showPasswords.confirm ? 'text' : 'password'}
            value={formData.confirm_password}
            onChange={(e) => setFormData({ ...formData, confirm_password: e.target.value })}
            className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-opacity-50"
            style={{ '--tw-ring-color': VCIN_COLORS.primary }}
            placeholder="Confirm new password"
            data-testid="confirm-password-input"
          />
          <button
            type="button"
            onClick={() => togglePasswordVisibility('confirm')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            {showPasswords.confirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <button
        type="submit"
        disabled={loading}
        className="w-full py-2.5 px-4 text-white font-medium rounded-lg transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
        style={{ backgroundColor: VCIN_COLORS.primary }}
        data-testid="change-password-btn"
      >
        {loading ? (
          <>
            <Loader2 className="w-4 h-4 animate-spin" />
            Changing Password...
          </>
        ) : (
          <>
            <Lock className="w-4 h-4" />
            Change Password
          </>
        )}
      </button>
    </form>
  );
}

export default function SAAccountPage() {
  const { user } = useAuth();

  // Get current time for session info
  const sessionStart = new Date();

  return (
    <div data-testid="sa-account-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Account</h1>
          <p className="text-sm text-gray-500 mt-1">Manage your profile and security settings</p>
        </div>
      </div>

      {/* Profile Details */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-gray-900">Profile Details</h2>
          <RoleBadge role={user?.role} />
        </div>

        <div className="divide-y divide-gray-100">
          <InfoRow 
            icon={User} 
            label="Full Name" 
            value={user?.full_name || 'Super Administrator'} 
          />
          <InfoRow 
            icon={Mail} 
            label="Email Address" 
            value={user?.email} 
          />
          <InfoRow 
            icon={Building2} 
            label="Organization" 
            value="VCIN - Vehicle Crime Intelligence Network" 
          />
          <InfoRow 
            icon={Shield} 
            label="Access Level" 
            value="Full Platform Access" 
          />
        </div>
      </div>

      {/* Change Password */}
      <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
        <div className="flex items-center gap-3 mb-6">
          <div 
            className="w-10 h-10 rounded-lg flex items-center justify-center"
            style={{ backgroundColor: `${VCIN_COLORS.primary}10` }}
          >
            <Lock className="w-5 h-5" style={{ color: VCIN_COLORS.primary }} />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Change Password</h2>
            <p className="text-sm text-gray-500">Update your account password</p>
          </div>
        </div>

        <ChangePasswordForm />
      </div>

      {/* Session Info */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-6">Current Session</h2>

        <div className="divide-y divide-gray-100">
          <InfoRow 
            icon={Calendar} 
            label="Session Started" 
            value={sessionStart.toLocaleString('en-GB', {
              day: '2-digit',
              month: 'long',
              year: 'numeric',
              hour: '2-digit',
              minute: '2-digit'
            })} 
          />
          <InfoRow 
            icon={Clock} 
            label="Status" 
            value={
              <span className="flex items-center gap-2">
                <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
                Active Session
              </span>
            } 
          />
        </div>

        <div className="mt-6 pt-6 border-t border-gray-100">
          <p className="text-sm text-gray-500">
            Your session is secured with 2FA authentication. For security reasons, 
            sessions automatically expire after 24 hours of inactivity.
          </p>
        </div>
      </div>
    </div>
  );
}
